/**
 * Switching the desktop app between Production and Preview.
 *
 * Rust keeps this channel's web storage, marks the target's for restore,
 * closes the other windows and restarts Denker on the target channel
 * (`release_channel_switch.rs`). Deep links name the server that built them;
 * one from the other channel asks before switching.
 */
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { translate } from "@/i18n";
import { releaseChannelMessages } from "@/i18n/messages/release-channel";
import {
  desktopChannel,
  linkChannel,
  type SwitchableChannel,
} from "@/lib/release-channel";
import { useNotificationStore } from "@/stores/notification-store";

const SWITCH_FOR_LINK_EVENT = "denker://release-channel-switch-for-link";
const LINK_PROMPT_ID = "release-channel-link";
const FAILURE_ID = "release-channel-switch-failed";
/** `MARKER_KEY` in `release_channel_restore.rs`. */
const RESTORE_MARKER_KEY = "denker.release-channel-restore";

let linkSwitchListener: Promise<unknown> | null = null;

export function channelName(channel: SwitchableChannel): string {
  return translate(releaseChannelMessages, channel);
}

/** Restart Denker on `target`, keeping this channel's web storage for later. */
export async function switchReleaseChannel(
  target: SwitchableChannel,
): Promise<void> {
  const storage: Record<string, string> = {};
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key !== null) storage[key] = localStorage.getItem(key) ?? "";
  }
  try {
    await invoke("release_channel_switch", { channel: target, storage });
  } catch (error) {
    useNotificationStore.getState().addSystemToast(() => ({
      id: FAILURE_ID,
      appWide: true,
      level: "error",
      title: translate(releaseChannelMessages, "switchFailed"),
      description: error instanceof Error ? error.message : String(error),
    }));
    throw error;
  }
}

/**
 * End the storage restore once it is safely on disk, so windows opened later
 * do not carry it. The restore writes its marker last, naming the restore and
 * the launch (`release_channel_restore.rs`). The webview saves storage to
 * disk some time later, so only a marker from an earlier launch shows the
 * whole copy survived a restart. Until then the restore stays pending, and
 * one that failed or was lost in a crash runs again on the next launch.
 */
export function confirmReleaseChannelRestore(): void {
  const restore = desktopChannel()?.restore;
  if (!restore) return;
  let marker: string | null;
  try {
    marker = localStorage.getItem(RESTORE_MARKER_KEY);
  } catch {
    return;
  }
  const [id, launch] = (marker ?? "").split(" ");
  if (id === restore.id && launch && launch !== restore.launch) {
    void invoke("release_channel_restored").catch(() => {});
  }
}

/**
 * Whether this app should handle a deep link. A link from the other
 * channel's server asks to switch instead; one from an unknown server is
 * ignored.
 */
export function acceptChannelLink(url: string): boolean {
  const target = linkChannel(url);
  if (target === "current") return true;
  if (target) askToSwitchForLink(target);
  return false;
}

function askToSwitchForLink(target: SwitchableChannel): void {
  linkSwitchListener ??= listen(SWITCH_FOR_LINK_EVENT, () => {
    useNotificationStore.getState().removeSystemToast(LINK_PROMPT_ID);
    void switchReleaseChannel(target).catch(() => {});
  });
  useNotificationStore.getState().addSystemToast(() => ({
    id: LINK_PROMPT_ID,
    appWide: true,
    level: "info",
    persistent: true,
    title: translate(releaseChannelMessages, "linkTitle", {
      channel: channelName(target),
    }),
    description: translate(releaseChannelMessages, "linkBody", {
      channel: channelName(target),
    }),
    actionLabel: translate(releaseChannelMessages, "switchAction"),
    actionTestId: "release-channel-link-switch",
    actionEvent: SWITCH_FOR_LINK_EVENT,
  }));
}
